// Modal Component - Handles trade details modal display
class ModalComponent {
    constructor() {
        this.modalOverlay = null;
        this.modalContent = null;
        this.modalTitle = null;
        this.modalBody = null;
        this.modalClose = null;
        this.isOpen = false;
        this.currentTrade = null;
        
        this.initializeModal();
    }

    /**
     * Initialize modal components
     */
    initializeModal() {
        // Modal elements removed - no longer used
        this.modalOverlay = null;
        this.modalTitle = null;
        this.modalBody = null;
        this.modalClose = null;
        
        if (!this.modalOverlay) return;

        this.setupEventListeners();
    }

    /**
     * Setup modal event listeners
     */
    setupEventListeners() {
        // Close button
        if (this.modalClose) {
            this.modalClose.addEventListener('click', () => this.close());
        }

        // Close on overlay click
        this.modalOverlay.addEventListener('click', (e) => {
            if (e.target === this.modalOverlay) {
                this.close();
            }
        });

        // Close on Escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
    }

    /**
     * Show trade details in modal
     * @param {Object} trade - Trade object to display
     */
    showTradeDetails(trade) {
        if (!trade) return;
        
        this.currentTrade = trade;
        this.renderTradeDetails(trade);
        this.open();
        
        // Request additional details from server if needed
        if (trade.id && !trade.clusters) {
            this.requestTradeDetails(trade.id);
        }
    }

    /**
     * Request additional trade details from server
     * @param {string|number} tradeId - Trade ID
     */
    async requestTradeDetails(tradeId) {
        try {
            await SSEService.requestTradeDetails(tradeId);
        } catch (error) {
            console.error('Failed to request trade details:', error);
            Helpers.showError('Failed to load additional trade details');
        }
    }

    /**
     * Update modal with detailed trade information
     * @param {Object} tradeDetails - Detailed trade object
     */
    updateTradeDetails(tradeDetails) {
        if (!this.isOpen || !this.currentTrade) return;
        
        // Merge details with current trade
        this.currentTrade = { ...this.currentTrade, ...tradeDetails };
        this.renderTradeDetails(this.currentTrade);
    }

    /**
     * Render trade details in modal body
     * @param {Object} trade - Trade object
     */
    renderTradeDetails(trade) {
        if (!this.modalTitle || !this.modalBody) return;

        // Set modal title
        this.modalTitle.textContent = `Trade Details - ${trade.asset} ${(trade.direction || '').toUpperCase()}`;

        // Clear modal body
        this.modalBody.innerHTML = '';

        // Create sections
        this.createBasicInfoSection(trade);
        this.createPriceInfoSection(trade);
        this.createRiskInfoSection(trade);
        this.createClusterInfoSection(trade);
        this.createTimestampSection(trade);
    }

    /**
     * Create basic information section
     * @param {Object} trade - Trade object
     */
    createBasicInfoSection(trade) {
        const section = this.createSection('Basic Information');
        const grid = this.createDetailGrid([
            { label: 'Asset', value: trade.asset || '-' },
            { label: 'Direction', value: this.formatDirection(trade.direction) },
            { label: 'Status', value: this.formatStatus(trade.status) },
            { label: 'Trade ID', value: trade.id || '-' }
        ]);
        
        section.appendChild(grid);
        this.modalBody.appendChild(section);
    }

    /**
     * Create price information section
     * @param {Object} trade - Trade object
     */
    createPriceInfoSection(trade) {
        const section = this.createSection('Price Information');
        const grid = this.createDetailGrid([
            { label: 'Entry Price', value: Helpers.formatCurrency(trade.entry_price, 6), highlight: true },
            { label: 'Target Price', value: Helpers.formatCurrency(trade.target_price, 6) },
            { label: 'Stop Price', value: Helpers.formatCurrency(trade.stop_price, 6) },
            { label: 'Current Price', value: Helpers.formatCurrency(trade.current_price, 6) }
        ]);
        
        section.appendChild(grid);
        this.modalBody.appendChild(section);
    }

    /**
     * Create risk information section
     * @param {Object} trade - Trade object
     */
    createRiskInfoSection(trade) {
        const section = this.createSection('Risk & Performance');
        
        // Calculate R/R from entry/TP/SL
        const entry = parseFloat(trade.entry_price);
        const tp = parseFloat(trade.target_price);
        const sl = parseFloat(trade.stop_price);
        
        let riskReward = 0;
        if (entry && tp && sl && !isNaN(entry) && !isNaN(tp) && !isNaN(sl)) {
            const reward = Math.abs(tp - entry);
            const risk = Math.abs(entry - sl);
            riskReward = risk > 0 ? (reward / risk) : 0;
        }
        const confidence = parseFloat(trade.confidence) || 0;
        const pnl = trade.unrealized_pnl || trade.pnl;
        
        const grid = this.createDetailGrid([
            { label: 'Risk/Reward Ratio', value: riskReward > 0 ? `${riskReward.toFixed(2)}:1` : '-', highlight: true },
            { label: 'Confidence', value: Helpers.formatPercentage(confidence), highlight: true },
            { label: 'Position Size', value: Helpers.formatCurrency(trade.position_size) },
            { label: 'Unrealized PnL', value: this.formatPnL(pnl) }
        ]);
        
        section.appendChild(grid);
        this.modalBody.appendChild(section);
    }

    /**
     * Create cluster information section
     * @param {Object} trade - Trade object
     */
    createClusterInfoSection(trade) {
        const section = this.createSection('Cluster Analysis');
        
        const clusterCount = parseInt(trade.cluster_count) || 0;
        const clusters = trade.clusters || [];
        
        const basicGrid = this.createDetailGrid([
            { label: 'Total Clusters', value: clusterCount.toString(), highlight: true },
            { label: 'Avg Cluster Size', value: trade.avg_cluster_size || '-' },
            { label: 'Max Cluster Size', value: trade.max_cluster_size || '-' },
            { label: 'Cluster Density', value: trade.cluster_density || '-' }
        ]);
        
        section.appendChild(basicGrid);
        
        // Add detailed cluster information if available
        if (clusters.length > 0) {
            const clusterList = this.createClusterList(clusters);
            section.appendChild(clusterList);
        }
        
        this.modalBody.appendChild(section);
    }

    /**
     * Create timestamp section
     * @param {Object} trade - Trade object
     */
    createTimestampSection(trade) {
        const section = this.createSection('Timeline');
        const grid = this.createDetailGrid([
            { label: 'Created', value: Helpers.formatDate(trade.created_at) },
            { label: 'Updated', value: Helpers.formatDate(trade.updated_at) },
            { label: 'Time Ago', value: Helpers.formatTimeAgo(trade.created_at) },
            { label: 'Duration', value: this.calculateDuration(trade.created_at) }
        ]);
        
        section.appendChild(grid);
        this.modalBody.appendChild(section);
    }

    /**
     * Create modal section
     * @param {string} title - Section title
     * @returns {HTMLElement} - Section element
     */
    createSection(title) {
        const section = document.createElement('div');
        section.className = 'modal-section';
        
        const header = document.createElement('h4');
        header.textContent = title;
        section.appendChild(header);
        
        return section;
    }

    /**
     * Create detail grid
     * @param {Array} items - Array of detail items
     * @returns {HTMLElement} - Grid element
     */
    createDetailGrid(items) {
        const grid = document.createElement('div');
        grid.className = 'detail-grid';
        
        items.forEach(item => {
            const detailItem = document.createElement('div');
            detailItem.className = 'detail-item';
            
            const label = document.createElement('div');
            label.className = 'detail-label';
            label.textContent = item.label;
            
            const value = document.createElement('div');
            value.className = `detail-value ${item.highlight ? 'highlight' : ''}`;
            value.textContent = item.value;
            
            detailItem.appendChild(label);
            detailItem.appendChild(value);
            grid.appendChild(detailItem);
        });
        
        return grid;
    }

    /**
     * Create cluster list
     * @param {Array} clusters - Array of cluster objects
     * @returns {HTMLElement} - Cluster list element
     */
    createClusterList(clusters) {
        const container = document.createElement('div');
        container.style.marginTop = '1rem';
        
        const title = document.createElement('h5');
        title.textContent = 'Cluster Details';
        title.style.marginBottom = '0.5rem';
        title.style.color = '#58a6ff';
        container.appendChild(title);
        
        clusters.slice(0, 5).forEach((cluster, index) => {
            const clusterItem = document.createElement('div');
            clusterItem.style.background = '#0D0F11';
            clusterItem.style.padding = '0.5rem';
            clusterItem.style.marginBottom = '0.5rem';
            clusterItem.style.borderRadius = '4px';
            clusterItem.style.fontSize = '0.875rem';
            
            // Create safe DOM elements instead of innerHTML
            const strong = document.createElement('strong');
            strong.textContent = `Cluster ${index + 1}: `;
            
            const priceText = document.createTextNode(Helpers.formatCurrency(cluster.price, 6));
            const sizeText = document.createTextNode(` (Size: ${cluster.size || '-'}, Distance: ${cluster.distance || '-'})`);
            
            clusterItem.appendChild(strong);
            clusterItem.appendChild(priceText);
            clusterItem.appendChild(sizeText);
            
            container.appendChild(clusterItem);
        });
        
        if (clusters.length > 5) {
            const moreText = document.createElement('div');
            moreText.style.color = '#7d8590';
            moreText.style.fontSize = '0.75rem';
            moreText.style.textAlign = 'center';
            moreText.textContent = `... and ${clusters.length - 5} more clusters`;
            container.appendChild(moreText);
        }
        
        return container;
    }

    /**
     * Format direction with badge
     * @param {string} direction - Trade direction
     * @returns {string} - Formatted direction
     */
    formatDirection(direction) {
        if (!direction) return '-';
        return `<span class="badge ${Helpers.getDirectionClass(direction)}">${direction.toUpperCase()}</span>`;
    }

    /**
     * Format status with appropriate styling
     * @param {string} status - Trade status
     * @returns {string} - Formatted status
     */
    formatStatus(status) {
        if (!status) return '-';
        
        const statusClass = {
            'active': 'badge-success',
            'expired': 'badge-warning',
            'archived': 'badge-secondary'
        }[status] || 'badge-secondary';
        
        return `<span class="badge ${statusClass}">${status.toUpperCase()}</span>`;
    }

    /**
     * Format PnL with color coding
     * @param {number} pnl - PnL value
     * @returns {string} - Formatted PnL
     */
    formatPnL(pnl) {
        if (pnl === null || pnl === undefined || isNaN(pnl)) return '-';
        
        const formatted = Helpers.formatCurrency(pnl, 2);
        const sign = pnl >= 0 ? '+' : '';
        return `${sign}${formatted}`;
    }

    /**
     * Calculate duration since trade creation
     * @param {string} createdAt - Creation timestamp
     * @returns {string} - Duration string
     */
    calculateDuration(createdAt) {
        if (!createdAt) return '-';
        
        const created = new Date(createdAt);
        const now = new Date();
        const diffMs = now - created;
        
        const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        if (days > 0) return `${days}d ${hours}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    }

    /**
     * Open modal
     */
    open() {
        if (!this.modalOverlay) return;
        
        this.modalOverlay.classList.add('active');
        this.isOpen = true;
        
        // Focus management
        setTimeout(() => {
            if (this.modalClose) {
                this.modalClose.focus();
            }
        }, CONFIG.UI.MODAL_CLOSE_DELAY);
    }

    /**
     * Close modal
     */
    close() {
        if (!this.modalOverlay) return;
        
        this.modalOverlay.classList.remove('active');
        this.isOpen = false;
        this.currentTrade = null;
        
        // Clear modal content after animation
        setTimeout(() => {
            if (this.modalBody) {
                this.modalBody.innerHTML = '';
            }
        }, CONFIG.UI.ANIMATION_DURATION);
    }

    /**
     * Check if modal is open
     * @returns {boolean} - True if modal is open
     */
    isModalOpen() {
        return this.isOpen;
    }

    /**
     * Get current trade being displayed
     * @returns {Object|null} - Current trade object
     */
    getCurrentTrade() {
        return this.currentTrade;
    }
}

// Export for ES6 modules
export { ModalComponent };

// Export singleton instance
window.ModalComponent = new ModalComponent();
